/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * UI domain - Modal management system for join and death modals
 * modals.client.ts - Centralized modal state and lifecycle management
 */

import {
  initPrettyStarfield,
  type StarfieldHandle,
} from "../effects/pretty-starfield";

let joinModalEl: HTMLElement | null = null;
let playerNameInputEl: HTMLInputElement | null = null;
let starfield: StarfieldHandle | null = null;

/**
 * Initialize modal element references
 * Must be called after DOM is loaded
 */
export function initializeModalElements(): void {
  joinModalEl = document.getElementById("joinModal");
  playerNameInputEl = document.getElementById(
    "playerNameInput",
  ) as HTMLInputElement;
}

/**
 * Initialize Canvas 2D starfield for landing page background
 * Simplified, fast, and looks great - no WebGL complexity needed
 */
export function initJoinModalStars(): void {
  const canvas = document.getElementById("webglStarfield") as HTMLCanvasElement;
  if (!canvas) {
    console?.warn?.("Starfield canvas not found");
    return;
  }

  if (starfield) {
    starfield.destroy();
  }

  starfield = initPrettyStarfield(canvas, {
    starCount: 900,
    maxSpeed: 30,
    tint: [120, 240, 255],
    background: "#000000",
    mouseParallax: true,
    grain: true,
    starburst: {
      pixelSnap: true,
      chromaticAberration: 0.8,
      vignette: 0.6,
      paletteMode: false, // Disabled - causes grey/brown tint on black background
      ditherEnabled: true, // Re-enabled (works fine without paletteMode)
    },
  });
}

/**
 * Show join modal with lobby browser
 */
export function showJoinModal(): void {
  if (joinModalEl) joinModalEl.classList.add("show");
  if (playerNameInputEl) playerNameInputEl.focus();

  const savedName = localStorage.getItem("playerName");
  if (savedName && playerNameInputEl) {
    playerNameInputEl.value = savedName;
  }

  // Use setTimeout to ensure modal is fully rendered
  setTimeout(() => {
    initJoinModalStars();
  }, 50);

  resetAutoJoinAttempt();
  startLobbyBrowser();
}

/**
 * Hide join modal and stop lobby updates
 */
export function hideJoinModal(): void {
  if (joinModalEl) joinModalEl.classList.remove("show");

  stopLobbyBrowser();

  // CRITICAL: Destroy starfield to stop rendering in background
  if (starfield) {
    starfield.destroy();
    starfield = null;
  }
}

/**
 * Handle "Join New Game" button from death modal
 */
export function handleJoinNewGame(): void {
  window.location.reload();
}

/**
 * Check if join modal is currently visible
 * @returns True if join modal is shown
 */
export function isJoinModalVisible(): boolean {
  return joinModalEl?.classList.contains("show") || false;
}

/**
 * Check if any modal is currently visible
 * @returns True if any modal is shown
 */
export function isAnyModalVisible(): boolean {
  return isJoinModalVisible();
}

let resetAutoJoinAttempt = (): void => {};
let startLobbyBrowser = (): void => {};
let stopLobbyBrowser = (): void => {};

/**
 * Set external dependencies from client.ts
 * @param deps - External dependency functions
 */
export function setModalDependencies(deps: {
  resetAutoJoinAttempt: () => void;
  startLobbyBrowser: () => void;
  stopLobbyBrowser: () => void;
  joinSpecificLobby: (lobbyId: string) => boolean;
  requestLobbyList: () => boolean;
}): void {
  resetAutoJoinAttempt = deps.resetAutoJoinAttempt;
  startLobbyBrowser = deps.startLobbyBrowser;
  stopLobbyBrowser = deps.stopLobbyBrowser;
}

/**
 * Initialize all modal systems
 * Should be called once after DOM is loaded
 */
export function initializeModals(): void {
  initializeModalElements();
  initJoinModalStars();
}

/**
 * Get modal system statistics for debugging
 * @returns Object with modal state information
 */
export function getModalStats(): {
  joinModalVisible: boolean;
  elementsInitialized: boolean;
} {
  return {
    joinModalVisible: isJoinModalVisible(),
    elementsInitialized: !!(joinModalEl && playerNameInputEl),
  };
}
